import { useMutation, useQueryClient } from "react-query";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IAttachment } from "../../common/models/IAttachment";
import AttachmentsDataService from "../../services/attachments-data-service/AttachmentsDataService";
import { IObjectAttachmentsModel } from "../../services/attachments-data-service/Models";
import { RestError } from "../../services/rest-service/RestService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";

export type UseSaveAttachmentParameters = {
    groupID: number,
    contextObject: IeLynxObject,
} & BaseUseMutationParameters<IAttachment | undefined, IAttachment>;

export function useSaveAttachment({
    groupID,
    contextObject,
    onError,
    onSuccess
}: UseSaveAttachmentParameters) {
    const queryClient = useQueryClient();

    return useMutation<IAttachment | undefined, RestError, IAttachment, MutationContextType>(
        (item) => {
            return AttachmentsDataService.saveAttachment(groupID, contextObject, item);
        },
        {
            onMutate: async (item: IAttachment) => {
                await queryClient.cancelQueries(keys.all);

                const previousAttachments = queryClient.getQueryData<IObjectAttachmentsModel>(
                    keys.attachments(
                        groupID,
                        contextObject
                    )
                );

                queryClient.setQueryData<IObjectAttachmentsModel>(
                    keys.attachments(
                        groupID,
                        contextObject
                    ),
                    (old?: IObjectAttachmentsModel) => {
                        const existingAttachment = old?.attachments?.find(a => a.id === item.id);

                        return existingAttachment
                            ? {
                                canEdit: !!old?.canEdit,
                                attachments: old?.attachments ?? []
                            }
                            : {
                                canEdit: !!old?.canEdit,
                                attachments: [...(old?.attachments ?? []), item]
                            };
                    }
                )

                return {
                    previousAttachments
                }
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _attachment, context) => {
                if (error && context?.previousAttachments) {
                    queryClient.setQueryData<IObjectAttachmentsModel>(
                        keys.attachments(
                            groupID,
                            contextObject
                        ),
                        context?.previousAttachments
                    );
                }

                queryClient.invalidateQueries(
                    keys.attachments(
                        groupID,
                        contextObject
                    )
                );
            }
        }
    );
}